var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import 'react-app-polyfill/ie11';
import React, { useState, useEffect } from "react";
import { Stage, Layer, Text } from "./react-konva";
import SeatPopup from "./components/SeatPopup";
import SeatMapKey from "./components/SeatMapKey";
import SeatPopupMulti from "./components/SeatPopupMulti";
import { Seat } from "./components/Seat";
import SVG from "./components/SVG";
import { SelectedTicketTable } from "./components/SelectedTicketTable";
import * as DOMData from './apis/DOMData';
import { useDispatch, useSelector } from "react-redux";
import { addToMultiTicketSelection, clearData, currentPerformanceRef, deleteTicketSelections, fetchInitialSeatmap, fetchSelectionsForPerformance, pollSeatmapUpdates, removeFromMultiTicketSelection, removeSeatFromBasket, selectComputedMultiSelection, selectComputedSeatmap, selectIsLoading, setMessage, } from "./store/seatmap";
import { calculateScaleToFitWithPadding, SEATMAP_PADDING, SEAT_RADIUS, useStageDimensions } from "./util";
import { useTranslation } from 'react-i18next';
var interval = null;
var SeatMap = function () {
    var _a, _b;
    var dispatch = useDispatch();
    var _c = useState(), cachedPerformanceRef = _c[0], setCachedPerformanceRef = _c[1];
    var _d = useState(true), isReservedPerformance = _d[0], setIsReservedPerformance = _d[1];
    useEffect(function () {
        var newPerfId = DOMData.getPerformanceId();
        if (newPerfId && newPerfId !== cachedPerformanceRef) {
            setCachedPerformanceRef(function (name) { return newPerfId; });
            dispatch(fetchInitialSeatmap(newPerfId));
        }
    }, []);
    useEffect(function () {
        setIsReservedPerformance(DOMData.isPerformanceReserved());
    }, []);
    var computedSeatMap = useSelector(selectComputedSeatmap);
    var loadingSeatmap = useSelector(selectIsLoading);
    var computedMultiSelection = useSelector(selectComputedMultiSelection);
    var _e = useStageDimensions(), stageRef = _e[0], stageSize = _e[1];
    var performanceRef = useSelector(currentPerformanceRef);
    var _f = useSelector(function (state) { return state.seatmap; }), boxSize = _f.boxSize, labels = _f.labels, ticketSelections = _f.ticketSelections, feedback = _f.feedback;
    var containerRef = React.useRef(null);
    var _g = useState({ primary: "#66a6d9", secondary: "#66A6D9" }), colours = _g[0], setColours = _g[1];
    var _h = useState({ seatId: null }), selectedSeat = _h[0], setSelectedSeat = _h[1];
    var clearSelectedSeat = function () { return setSelectedSeat({ seatId: null }); };
    var _j = React.useState({
        width: 1000,
        height: 1000,
    }), size = _j[0], setSize = _j[1];
    var _k = React.useState(6), scaleToFit = _k[0], setScaleToFit = _k[1];
    var _l = useState(6), scale = _l[0], setScale = _l[1];
    var zoomIn = function () {
        setScale(Math.min(scale + 1, 8));
    };
    var zoomOut = function () {
        if ((scale - 1) <= scaleToFit) {
            resetZoom();
        }
        else {
            setScale(scale - 1);
        }
    };
    var resetZoom = function () {
        var _a;
        setScale(scaleToFit);
        (_a = stageRef.current) === null || _a === void 0 ? void 0 : _a.position({ x: 0, y: 0 });
    };
    /**
     * When clicking on the seat circle
     */
    var handleSeatSelect = function (seatId, position) {
        if (computedMultiSelection.enabled) {
            dispatch(addToMultiTicketSelection({ concessionId: computedMultiSelection.concession.id, priceBandId: computedMultiSelection.priceBand.id, seatId: seatId }));
        }
        else {
            setSelectedSeat({ seatId: seatId, position: position });
        }
    };
    var handleDeselectSeat = function (seatId) {
        if (computedMultiSelection.enabled) {
            dispatch(removeFromMultiTicketSelection({ seatIds: [seatId] }));
        }
        else {
            var _a = computedSeatMap[seatId], state = _a.state, priceBand = _a.priceBand;
            if (state === 'error') {
                dispatch(deleteTicketSelections([seatId]));
            }
            else {
                var concession = priceBand.concessions.find(function (_a) {
                    var id = _a.id;
                    return id === ticketSelections[seatId].item.concessionId;
                });
                if (!concession || concession.minimumQty <= 1) {
                    dispatch(removeSeatFromBasket(seatId));
                }
                else {
                    dispatch(setMessage({ isError: true, message: 'Tickets in this concession can only be removed on the basket page.' }));
                }
            }
        }
    };
    useEffect(clearSelectedSeat, [scale]);
    useEffect(function () {
        dispatch(fetchSelectionsForPerformance());
    }, [performanceRef]);
    useEffect(function () {
        var _a, _b, _c, _d;
        if (!containerRef.current) {
            return;
        }
        var newSize = {
            width: (_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 1000,
            height: (_d = (_c = containerRef.current) === null || _c === void 0 ? void 0 : _c.offsetHeight) !== null && _d !== void 0 ? _d : 1000,
        };
        if (newSize.width === 0 || newSize.width !== size.width || newSize.height !== size.height) {
            setSize(newSize);
        }
    });
    useEffect(function () {
        if (!stageRef.current) {
            return;
        }
        window.stageRef = stageRef;
        var stageSize = stageRef.current.getClientRect({ skipTransform: false });
        var newScale = calculateScaleToFitWithPadding(stageSize, size, scale, SEATMAP_PADDING);
        setScaleToFit(newScale);
    }, [size, setScaleToFit, computedSeatMap]);
    useEffect(function () {
        setScale(scaleToFit);
    }, [scaleToFit, setScale]);
    var _m = useState(undefined), font = _m[0], setFont = _m[1];
    React.useEffect(function () {
        var _a;
        var _b = DOMData.getRed61Config(), primary_color = _b.primary_color, secondary_color = _b.secondary_color, fonts = _b.fonts;
        setFont((_a = fonts === null || fonts === void 0 ? void 0 : fonts.split(':')[0]) !== null && _a !== void 0 ? _a : undefined);
        setColours({ primary: primary_color !== null && primary_color !== void 0 ? primary_color : colours.primary, secondary: secondary_color !== null && secondary_color !== void 0 ? secondary_color : colours.secondary });
    }, [setColours]);
    React.useEffect(function () { return function () { dispatch(clearData()); }; }, []);
    useEffect(function () {
        if (computedSeatMap) {
            window['seatmapControls'] = {
                selectSeat: handleSeatSelect,
                selectFirstAvailableSeat: (function (priceBandId) {
                    var seats = Object.values(computedSeatMap).filter(function (_a) {
                        var state = _a.state, priceBand = _a.priceBand;
                        return state === 'available' && priceBand.id === priceBandId;
                    });
                    var firstFreeSeat = seats[Math.floor(Math.random() * seats.length)];
                    if (firstFreeSeat) {
                        handleSeatSelect(firstFreeSeat.seat.id, { x: 20, y: 20 });
                    }
                })
            };
        }
    }, [computedSeatMap]);
    var intervalInSecondsOfSeatmapPollingUpdating = parseInt(window.red61.seatmap_interval_refresh_timer + "000");
    useEffect(function () {
        if (!loadingSeatmap && !!cachedPerformanceRef && !!isReservedPerformance) {
            interval = setInterval(function () {
                dispatch(pollSeatmapUpdates());
            }, intervalInSecondsOfSeatmapPollingUpdating);
            return function () { return clearInterval(interval); };
        }
    }, [loadingSeatmap, cachedPerformanceRef, isReservedPerformance]);
    // clean interval when leaving the event->performance page
    useEffect(function () {
        if (window.location.pathname.split('event')[1].split('/').length === 4 && window.location.pathname.includes('event'))
            return;
        clearInterval(interval);
    });
    var _o = useTranslation('event', { useSuspense: false }), t = _o.t, i18n = _o.i18n, ready = _o.ready;
    if (loadingSeatmap || performanceRef !== cachedPerformanceRef || !ready) {
        return _jsx("div", __assign({ ref: containerRef }, { children: "Loading..." }));
    }
    function getPolyLangUrl() {
        var polyLangUrl = window.location.href.split('event')[0];
        return polyLangUrl + 'basket';
    }
    return (_jsxs(_Fragment, { children: [_jsx(SeatMapKey, { computedSeatMap: computedSeatMap }), _jsxs("div", __assign({ className: "Seatmap-detail" }, { children: [_jsx("div", __assign({ className: "seatmap-zoom" }, { children: _jsxs("ul", { children: [_jsx("li", { children: _jsx("button", __assign({ className: "zoom-reset", onClick: resetZoom }, { children: _jsx(SVG, { icon: "reset", primaryColour: colours.primary }) })) }), _jsx("li", { children: _jsx("button", __assign({ className: "zoom-in", onClick: zoomIn }, { children: _jsx(SVG, { icon: "zoom-in", primaryColour: colours.primary }) })) }), _jsx("li", { children: _jsx("button", __assign({ className: "zoom-out", onClick: zoomOut }, { children: _jsx(SVG, { icon: "zoom-out", primaryColour: colours.primary }) })) })] }) })), _jsxs("div", __assign({ style: {
                            position: "relative",
                            backgroundColor: "white",
                            width: "100%",
                            border: "1px solid #e2e2e2",
                            height: (boxSize.height / boxSize.width) * ((_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth) !== null && _b !== void 0 ? _b : 100),
                            minHeight: "220px"
                        }, ref: containerRef }, { children: [computedMultiSelection.enabled && (_jsx(_Fragment, { children: _jsx(SeatPopupMulti, { selections: computedMultiSelection.seats, concession: computedMultiSelection.concession, error: feedback.isError ? feedback.message : undefined }) })), _jsx(Stage, __assign({ ref: stageRef, width: size.width, height: size.height, scaleX: scale, scaleY: scale, draggable: scale !== scaleToFit, onDragStart: clearSelectedSeat }, { children: _jsxs(Layer, __assign({ x: (SEAT_RADIUS + SEATMAP_PADDING), y: (SEAT_RADIUS + SEATMAP_PADDING) }, { children: [labels.map(function (label, index) { return (_jsx(Text, { x: label.x, y: label.y, text: label.title, rotation: label.rotateAngle, fontFamily: font }, index)); }), Object.values(computedSeatMap).map(function (_a) {
                                            var seat = _a.seat, priceBand = _a.priceBand, state = _a.state;
                                            return (_jsx(Seat, { seatData: seat, priceBandData: priceBand, state: state, onSelect: handleSeatSelect, onDeselect: handleDeselectSeat, selectedColour: "white" }, seat.id));
                                        })] })) })), selectedSeat.seatId && selectedSeat.position && (_jsx(_Fragment, { children: _jsx(SeatPopup, { position: selectedSeat.position, seat: computedSeatMap[selectedSeat.seatId].seat, priceBand: computedSeatMap[selectedSeat.seatId].priceBand, onClose: clearSelectedSeat }) }))] })), feedback && feedback.message && !computedMultiSelection.enabled && (_jsx("div", __assign({ className: "alert " + (feedback.isError ? "alert-danger" : "alert-success"), role: "alert" }, { children: feedback.message }))), computedSeatMap && ticketSelections && (_jsx(SelectedTicketTable, { computedSeatMap: computedSeatMap, ticketSelections: Object.values(ticketSelections), handleDeselectSeat: handleDeselectSeat })), _jsx("a", __assign({ href: getPolyLangUrl() }, { children: _jsx("button", __assign({ className: "checkout-button", disabled: !Object.keys(ticketSelections).length || Object.values(ticketSelections).find(function (selection) { return selection.status !== 'success'; }) !== undefined }, { children: t('Checkout') })) }))] }))] }));
};
export default SeatMap;
